/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps } from '@msdyn365-commerce/core';
import { ProductList } from '@msdyn365-commerce/retail-proxy';
import { updateOrderTemplateHeader, UpdateOrderTemplateHeaderInput } from '@msdyn365-commerce-modules/retail-actions';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';

export interface IShareOrderTemplateDialogResources {
    shareOrderTemplateHeader: string;
    unShareOrderTemplateHeader: string;

    shareOrderTemplatesDescription: string;
    unshareOrderTemplatesDescription: string;

    shareOrderTemplateButtonText: string;
    unshareOrderTemplateButtonText: string;
    cancelOrderTemplateShareButtonText: string;
}

export interface IShareOrderTemplateDialogProps extends IComponentProps {
    className: string;
    modalOpen: boolean | undefined;
    setModalOpen(newValue: boolean): void;
    isShared: boolean;
    productList: ProductList;
    onSuccess(orderTemplate: ProductList): Promise<void>;
    resources: IShareOrderTemplateDialogResources;
}

export const ShareOrderTemplateDialog = (props: IShareOrderTemplateDialogProps) => {
    const { modalOpen, setModalOpen, resources, onSuccess, isShared, productList, context } = props;
    const headerText = isShared ? resources.unShareOrderTemplateHeader : resources.shareOrderTemplateHeader;
    const bodyText = isShared ? resources.unshareOrderTemplatesDescription : resources.shareOrderTemplatesDescription;
    const buttonText = isShared ? resources.unshareOrderTemplateButtonText : resources.shareOrderTemplateButtonText;
    const [isLoading, setIsLoading] = useState(false);

    const closeDialog = () => {
        setModalOpen(false);
    };

    const clickConfirm = async () => {
        setIsLoading(true);
        const updatedProductList = { ...productList };
        updatedProductList.IsShared = !isShared;
        try {
            const input = new UpdateOrderTemplateHeaderInput(updatedProductList);
            const result = await updateOrderTemplateHeader(input, context.actionContext);
            setModalOpen(false);
            await onSuccess(result);
        } catch (error) {
            context?.telemetry?.error('Failed to update order template sharing status', error);
            setModalOpen(false);
        } finally {
            setIsLoading(false);
        }
    };

    const className = 'msc-order-template-name-dialog';

    return (
        <>
            <Modal isOpen={modalOpen} className={`${className} msc-modal-input-required`} toggle={closeDialog}>
                <ModalHeader className={`${className}__dialog__header`} toggle={closeDialog}>
                    <span>{headerText}</span>
                </ModalHeader>
                <ModalBody className={`${className}__dialog__body`}>
                    <div className={`${className}__dialog__content`}>
                        <div className={`${className}__dialog__share-templates-description`}>{bodyText}</div>
                    </div>
                </ModalBody>
                <ModalFooter className={`${className}__dialog__footer`}>
                    <Button
                        className={`${className}__dialog__share-order-template-button`}
                        title={buttonText}
                        onClick={clickConfirm}
                        disabled={isLoading}
                    >
                        {buttonText}
                    </Button>
                    <Button
                        className={`${className}__dialog__cancel-button`}
                        title={resources.cancelOrderTemplateShareButtonText}
                        onClick={closeDialog}
                    >
                        {resources.cancelOrderTemplateShareButtonText}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};
